import React, { useEffect, useState } from 'react';
import TabelaCrud from '../components/TabelaCrud';
import api from '../services/api';

/**
 * Cadastro de TURMAS — montadas a partir das salas já cadastradas.
 *
 * A creche escolhe uma das turmas-modelo com lastro legal que o SaaS oferece, ou
 * monta a sua. ⚖️ Modelo é prateleira, não cerca: nada aqui impede a creche de
 * fazer diferente — o sistema avisa e acompanha.
 */

interface Turma {
  id: number;
  nome: string;
  faixa_etaria: string;
  modalidade: string | null;
  descricao_atendimento: string | null;
  turno: string;
  capacidade_maxima: number;
  criancas_por_educador: number | null;
  area_m2: number | null;
  educadores_alocados: number;
  id_sala: number | null;
  modelo: string | null;
  idade_minima_meses: number | null;
  idade_maxima_meses: number | null;
}

interface FaixaModelo {
  codigo: string;
  nome: string;
  etapa: string;
  idade_minima_meses: number;
  idade_maxima_meses: number;
  criancas_por_educador: number;
  idade_legivel: string;
  base_legal: string;
}

interface ModeloTurma {
  codigo: string;
  faixa: string;
  nome: string;
  idade_minima_meses: number;
  idade_maxima_meses: number;
  idade_legivel: string;
  educadores_alocados: number;
  criancas_por_educador: number;
  capacidade_maxima: number;
  base_legal: string;
}

interface Sala {
  id: number;
  nome: string;
  uso: string;
  uso_rotulo?: string;
  capacidade_criancas: number | null;
}

const TURNOS = [
  { value: 'integral', label: 'Integral' },
  { value: 'matutino', label: 'Matutino' },
  { value: 'vespertino', label: 'Vespertino' },
];

const MODELO_PROPRIO = 'proprio';

/** Educadores necessários para N crianças, pelo limite legal da faixa. */
function educadoresNecessarios(criancas: number, limite: number | null) {
  if (!criancas || !limite) return null;
  return Math.ceil(criancas / limite);
}

export default function TurmasPage() {
  const [faixas, setFaixas] = useState<FaixaModelo[]>([]);
  const [modelos, setModelos] = useState<ModeloTurma[]>([]);
  const [salas, setSalas] = useState<Sala[]>([]);

  useEffect(() => {
    api
      .get<{ faixas: FaixaModelo[]; modelos: ModeloTurma[] }>('/turmas/modelos')
      .then(({ data }) => {
        setFaixas(data.faixas || []);
        setModelos(data.modelos || []);
      })
      .catch(() => {});
    api
      .get<Sala[]>('/salas')
      .then(({ data }) => setSalas(Array.isArray(data) ? data : []))
      .catch(() => setSalas([]));
  }, []);

  const nomeDaSala = (id: number | null) =>
    salas.find((s) => s.id === id)?.nome || '—';

  const faixaDoModelo = (codigo: string | null) => {
    const m = modelos.find((x) => x.codigo === codigo);
    return m ? faixas.find((f) => f.codigo === m.faixa) : undefined;
  };

  return (
    <TabelaCrud<Turma>
      titulo="Turmas"
      endpoint="/turmas"
      modalLargo
      colunas={[
        { chave: 'nome', label: 'Nome' },
        {
          chave: 'modelo',
          label: 'Modelo',
          render: (t) =>
            t.modelo && t.modelo !== MODELO_PROPRIO
              ? faixaDoModelo(t.modelo)?.nome || t.modelo
              : 'Própria',
        },
        {
          chave: 'idade_minima_meses',
          label: 'Idade',
          render: (t) =>
            t.idade_minima_meses != null && t.idade_maxima_meses != null
              ? `${t.idade_minima_meses}–${t.idade_maxima_meses} meses`
              : '—',
        },
        { chave: 'id_sala', label: 'Sala', render: (t) => nomeDaSala(t.id_sala) },
        {
          chave: 'turno',
          label: 'Turno',
          render: (t) => TURNOS.find((x) => x.value === t.turno)?.label || t.turno,
        },
        { chave: 'capacidade_maxima', label: 'Capac.' },
        { chave: 'educadores_alocados', label: 'Educadores' },
      ]}
      itemVazio={{
        nome: '',
        modelo: MODELO_PROPRIO,
        faixa_etaria: 'b1',
        descricao_atendimento: '',
        turno: 'integral',
        capacidade_maxima: 0,
        criancas_por_educador: null,
        idade_minima_meses: null,
        idade_maxima_meses: null,
        educadores_alocados: 0,
        id_sala: null,
      }}
      validateItem={(item) => {
        if (!item.nome || !String(item.nome).trim()) return 'Informe o nome da turma.';
        if (!item.id_sala) {
          return salas.length
            ? 'Escolha a sala em que a turma será atendida.'
            : 'Cadastre ao menos uma sala antes de criar turmas — a turma é montada a partir da sala.';
        }
        return null;
      }}
      renderFormulario={(item, onChange) => {
        const sala = salas.find((s) => s.id === Number(item.id_sala));
        const faixa = faixaDoModelo(item.modelo ?? null);
        const limiteLegal = faixa?.criancas_por_educador ?? null;
        const capacidade = Number(item.capacidade_maxima) || 0;
        const sugeridos = educadoresNecessarios(capacidade, limiteLegal);
        const excedeSala =
          sala?.capacidade_criancas != null &&
          capacidade > 0 &&
          capacidade > sala.capacidade_criancas;

        /** Adotar um modelo preenche os campos; a creche pode alterar depois. */
        function aplicarModelo(codigo: string) {
          onChange('modelo', codigo);
          const m = modelos.find((x) => x.codigo === codigo);
          if (!m) return;
          onChange('idade_minima_meses', m.idade_minima_meses);
          onChange('idade_maxima_meses', m.idade_maxima_meses);
          onChange('criancas_por_educador', m.criancas_por_educador);
          onChange('educadores_alocados', m.educadores_alocados);
          onChange('capacidade_maxima', m.capacidade_maxima);
        }

        return (
          <>
            {salas.length === 0 && (
              <div className="alerta alerta-atencao campo-largo">
                Nenhuma sala cadastrada. A turma é montada a partir da sala —
                cadastre as salas primeiro, no menu <b>Salas</b>.
              </div>
            )}

            <label className="campo-largo">
              Turma-modelo
              <select
                value={item.modelo ?? MODELO_PROPRIO}
                onChange={(e) => aplicarModelo(e.target.value)}
              >
                <option value={MODELO_PROPRIO}>
                  Turma própria (a creche define tudo)
                </option>
                {faixas.map((f) => (
                  <optgroup key={f.codigo} label={`${f.nome} — ${f.idade_legivel}`}>
                    {modelos
                      .filter((m) => m.faixa === f.codigo)
                      .map((m) => (
                        <option key={m.codigo} value={m.codigo}>
                          {m.nome}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
              {faixa ? (
                <small>{faixa.base_legal}</small>
              ) : (
                <small>
                  Os modelos seguem o parâmetro nacional. Adotar é opcional: a creche
                  pode montar a turma do seu jeito, e o sistema apenas acompanha.
                </small>
              )}
            </label>

            <label>
              Nome
              <input
                value={item.nome ?? ''}
                onChange={(e) => onChange('nome', e.target.value)}
                placeholder={sala ? `Ex.: ${faixa?.nome || 'Turma'} — ${sala.nome}` : 'Nome da turma'}
              />
            </label>

            <label>
              Sala
              <select
                value={item.id_sala ?? ''}
                onChange={(e) =>
                  onChange('id_sala', e.target.value === '' ? null : Number(e.target.value))
                }
              >
                <option value="">Selecione a sala…</option>
                {salas.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nome}
                    {s.capacidade_criancas ? ` (até ${s.capacidade_criancas})` : ''}
                    {' — '}
                    {s.uso_rotulo || s.uso}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Turno
              <select
                value={item.turno ?? 'integral'}
                onChange={(e) => onChange('turno', e.target.value)}
              >
                {TURNOS.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </label>

            <label>
              Idade mínima (meses)
              <input
                type="number"
                min={0}
                value={item.idade_minima_meses ?? ''}
                onChange={(e) =>
                  onChange('idade_minima_meses', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </label>

            <label>
              Idade máxima (meses)
              <input
                type="number"
                min={0}
                value={item.idade_maxima_meses ?? ''}
                onChange={(e) =>
                  onChange('idade_maxima_meses', e.target.value === '' ? null : Number(e.target.value))
                }
              />
            </label>

            <label>
              Capacidade da turma (crianças)
              <input
                type="number"
                min={0}
                value={item.capacidade_maxima ?? 0}
                onChange={(e) => onChange('capacidade_maxima', Number(e.target.value))}
              />
              {excedeSala && (
                <small style={{ color: '#b45309' }}>
                  ⚠️ A sala {sala?.nome} está cadastrada para{' '}
                  {sala?.capacidade_criancas} crianças por período. A creche pode
                  operar assim — o sistema registra e acompanha.
                </small>
              )}
            </label>

            <label>
              Educadores alocados
              <input
                type="number"
                min={0}
                value={item.educadores_alocados ?? 0}
                onChange={(e) => onChange('educadores_alocados', Number(e.target.value))}
              />
              {sugeridos != null && (
                <small>
                  Para {capacidade} crianças nesta faixa, o parâmetro de {limiteLegal}{' '}
                  por educador indica <b>{sugeridos}</b>{' '}
                  {sugeridos === 1 ? 'educador' : 'educadores'}.
                </small>
              )}
            </label>

            <label>
              Crianças por educador
              <input
                type="number"
                min={0}
                value={item.criancas_por_educador ?? ''}
                onChange={(e) =>
                  onChange('criancas_por_educador', e.target.value === '' ? null : Number(e.target.value))
                }
                placeholder={limiteLegal ? String(limiteLegal) : 'Ex.: 6'}
              />
              <small>Limite adotado pela creche para esta turma.</small>
            </label>

            <label className="campo-largo">
              Descrição do atendimento
              <textarea
                rows={2}
                value={item.descricao_atendimento ?? ''}
                onChange={(e) => onChange('descricao_atendimento', e.target.value)}
                placeholder="Rotina, proposta pedagógica, particularidades da turma"
              />
            </label>
          </>
        );
      }}
    />
  );
}
